import { getDbSafe } from '../services/firebaseConfig';
import { doc, runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';
import { applyGoldCredit } from './goldCore';

export const GoldService = {
    /**
     * Claim actual daily streak gold.
     * Logic:
     * 1. Check last claim time.
     * 2. If missed a day (outside 24-48h window? or just check date?), logic says "strict 24h cadence", but easier is checking calendar day difference.
     *    Let's assume "server timestamps for last claim" and logic:
     *    - If last claim was yesterday (1 calendar day ago): increment streak.
     *    - If last claim was today: fail (already claimed).
     *    - If last claim was >1 day ago: reset to Day 1.
     * 3. Calculate amount based on day (1..7).
     * 4. Apply multiplier (Yapper x2, Elite x3).
     * 5. Update: goldBalance, goldStreakDay, goldLastClaimAt.
     */
    async claimDailyStreak(userId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);

        try {
            return await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) throw new Error("User not found");

                const data = userDoc.data();
                const now = new Date();
                const lastClaimAt = data.goldLastClaimAt ? data.goldLastClaimAt.toDate() : null;

                let streakDay = data.goldStreakDay || 1;

                // Window validation
                if (lastClaimAt) {
                    const hoursSinceLastClaim = (now.getTime() - lastClaimAt.getTime()) / (1000 * 60 * 60);

                    if (hoursSinceLastClaim < 24) {
                        throw new Error("Daily reward already claimed. Come back later.");
                    }

                    if (hoursSinceLastClaim >= 48) {
                        // Missed a day, reset
                        streakDay = 1;
                    } else {
                        // Continued streak
                        streakDay += 1;
                        // Reset cycle after completing Day 7
                        if (streakDay > 7) {
                            streakDay = 1;
                        }
                    }
                } else {
                    // First valid claim
                    streakDay = 1;
                }

                // Base amounts
                const rewards = [25, 50, 100, 200, 300, 400, 500];
                const baseAmount = rewards[streakDay - 1];

                // Multiplier
                let multiplier = 1;
                const tier = data.subscriptionTier || 'none';
                if (tier === 'yapper') multiplier = 2;
                if (tier === 'elite') multiplier = 3;

                const finalAmount = baseAmount * multiplier;

                applyGoldCredit(transaction, userRef, data, finalAmount, {
                    goldStreakDay: streakDay,
                    goldLastClaimAt: serverTimestamp(),
                });

                return { success: true, amount: finalAmount, streakDay };
            });

        } catch (e: any) {
            console.error("Claim Error:", e);
            throw e;
        }
    },

    /**
     * Reward Ad Watch
     * Specs:
     * - 10 gold per ad
     * - Max 10/day (goldAdClaimsDateKey YYYY-MM-DD)
     * - Max 5/hour (goldAdClaimsHourKey YYYY-MM-DD-HH)
     */
    async rewardAd(userId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const now = new Date();
        const dateKey = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const hourKey = `${dateKey}-${now.getHours()}`; // YYYY-MM-DD-HH

        try {
            await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) throw new Error("User not found");
                const data = userDoc.data();

                // Check Daily Cap
                const lastDateKey = data.goldAdClaimsDateKey;
                let dailyCount = (lastDateKey === dateKey) ? (data.goldAdClaimsToday || 0) : 0;

                if (dailyCount >= 10) {
                    throw new Error("Daily ad limit reached (10/day).");
                }

                // Check Hourly Cap
                const lastHourKey = data.goldAdClaimsHourKey;
                let hourlyCount = (lastHourKey === hourKey) ? (data.goldAdClaimsThisHour || 0) : 0;

                if (hourlyCount >= 5) {
                    throw new Error("Hourly ad limit reached (5/hour).");
                }

                const reward = 10;
                applyGoldCredit(transaction, userRef, data, reward, {
                    goldAdClaimsDateKey: dateKey,
                    goldAdClaimsToday: dailyCount + 1,
                    goldAdClaimsHourKey: hourKey,
                    goldAdClaimsThisHour: hourlyCount + 1,
                });
            });
            return { success: true, reward: 10 };
        } catch (e) {
            console.error("Ad Reward Error:", e);
            throw e;
        }
    },

    // NOTE: spendGold() used to live here — a third spend implementation with no
    // callers, no audit entry, and its own balance-read convention. Removed as
    // part of the gold consolidation; standalone spends go through
    // EconomyService.deductGold, atomic ones through applyGoldSpend().
};
